import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { authedRequest, unauthedV2Request, unauthedV3Request } from "utils/api/requests";
import { getFetchParams } from "utils/helpers";
import { getUnlockToken } from "./mutations";
import { env } from "utils/env";
import type { components } from "api/schema";

/** Error shape thrown by the request helper; carries an HTTP status code. */
interface ApiError extends Error {
  statusCode?: number;
}

/**
 * Schema-derived types (source of truth: movebooth-rails openapi.yaml, vendored
 * at src/api/openapi.snapshot.yaml, generated into src/api/schema.d.ts via
 * `yarn openapi:gen`). If a field these consumers depend on is removed or
 * renamed in the spec, `yarn typecheck` (tsc --noEmit) fails in CI.
 */
type V3Gif = components["schemas"]["V3Gif"];
type V3GallerySuccessResponse = components["schemas"]["V3GallerySuccessResponse"];
type V3GalleryEmptyResponse = components["schemas"]["V3GalleryEmptyResponse"];
type V3ShowChannelResponse = components["schemas"]["V3ShowChannelResponse"];
type ChannelV2PublicDetailResponse =
  components["schemas"]["ChannelV2PublicDetailResponse"];
type ChannelV2DetailResponse = components["schemas"]["ChannelV2DetailResponse"];

/**
 * The raw v3 load-gifs payload is one of two shapes (success or empty-gallery).
 * The empty-gallery branch carries `channel` + `message` and no `pagination`.
 */
type V3GalleryResponse = V3GallerySuccessResponse | V3GalleryEmptyResponse;

/** Shape returned to the gallery UI (v2-compatible), regardless of API version. */
interface GalleryViewModel {
  current_page: number;
  page_count: number;
  total_count: number;
  has_more?: boolean;
  gifs: Array<Record<string, unknown>>;
  channel: Record<string, unknown>;
}

interface GalleryQueryParams {
  page?: number | string;
  start?: string;
  end?: string;
  unlock_token?: string;
  server_session_id?: string;
  [key: string]: unknown;
}

/**
 * Feature flag for V3 API usage.
 *
 * This is a BUILD-TIME configuration. To change API version:
 * 1. Set VITE_USE_V3_API=false in environment
 * 2. Rebuild the application (npm run build)
 * 3. Deploy the new build
 *
 * Defaults to true (V3 API) if not set.
 */
const USE_V3_API = env.VITE_USE_V3_API !== "false";

export const useGetGallery = ({
  slug,
  params,
}: {
  slug: string;
  params?: GalleryQueryParams;
}) => {
  // Get channel-specific unlock token
  const unlock_token = getUnlockToken(slug);

  // Determine which endpoint and auth param to use
  const useV3 = USE_V3_API;
  const endpoint = useV3 ? `v3/channels/${slug}/load-gifs` : `v2/channels/${slug}/load-gifs`;
  const requestFn = useV3 ? unauthedV3Request : unauthedV2Request;

  // Build query params based on API version
  const queryParams: GalleryQueryParams = { ...params };
  if (useV3) {
    // v3 uses unlock_token and 0-based page indexing
    if (unlock_token) {
      queryParams.unlock_token = unlock_token;
    }
    // v3 API uses 0-based page indexing (page 0 is the first page)
    // UI uses 0-based indexing as well, so no conversion needed
    // (removing the -1 conversion that was causing duplicates)
  } else {
    // v2 uses server_session_id and 1-based page indexing
    if (unlock_token) {
      queryParams.server_session_id = unlock_token;
    }
  }

  const queryString = getFetchParams(queryParams);

  return useQuery<unknown, ApiError, GalleryViewModel>({
    // query key changing triggers new requests (or loads cached data if that request has been made already)
    queryKey: [
      "gallery",
      slug,
      params?.page?.toString(),
      params?.start?.toString(),
      params?.end?.toString(),
      useV3 ? "v3" : "v2", // Include API version in cache key
    ],
    queryFn: () =>
      requestFn(`${endpoint}${queryString}`, {
        method: "GET",
      }),
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) — they won't succeed on retry
      if (error?.statusCode && error.statusCode >= 400 && error.statusCode < 500) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: (data: unknown): GalleryViewModel => {
      if (useV3) {
        // Transform v3 response to v2 format
        return transformV3Response(data as V3GalleryResponse, params);
      }

      // v2 format (existing logic)
      const v2 = data as {
        current_page: number;
        page_count: number;
        total_count?: number;
        data: { gifs?: Array<Record<string, unknown>> } & Record<string, unknown>;
      };
      return {
        current_page: v2.current_page,
        page_count: v2.page_count,
        total_count: v2.total_count ?? 0,
        gifs: v2.data.gifs || [],
        channel: v2.data || {},
      };
    },
  });
};

/**
 * The channel identity/branding a page can render without any gif list.
 */
export interface ChannelInfo {
  id?: number;
  name?: string;
  slug?: string;
  gallery_color?: string | null;
  app_background_color?: string | null;
  gallery_banner?: string;
  big_logo_image_url?: string;
  header?: string | null;
  header_color?: string | null;
}

/**
 * Channel detail with no gifs attached — `GET /api/v{2,3}/channels/:identifier`.
 *
 * Both versions are public and accept a slug or a numeric id. Use this instead
 * of `useGetGallery` wherever a page needs the channel's name, branding or
 * canonical slug but renders no gallery grid; `useGetGallery` would serve a
 * whole page of gif records to supply the same handful of scalars.
 *
 * Unlike the gallery endpoint this is NOT gated by the channel password — the
 * password protects gallery CONTENT, not channel config — so it resolves for
 * protected channels too.
 */
export const useGetChannelInfo = (identifier?: string) => {
  const useV3 = USE_V3_API;
  const endpoint = useV3 ? `v3/channels/${identifier}` : `v2/channels/${identifier}`;
  const requestFn = useV3 ? unauthedV3Request : unauthedV2Request;

  return useQuery<unknown, ApiError, ChannelInfo>({
    queryKey: ["channelInfo", identifier, useV3 ? "v3" : "v2"],
    queryFn: () => requestFn(endpoint, { method: "GET" }),
    enabled: !!identifier,
    retry: (failureCount, error) => {
      // Same policy as the gallery query: a 4xx won't succeed on retry.
      if (error?.statusCode && error.statusCode >= 400 && error.statusCode < 500) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: (data: unknown): ChannelInfo => {
      const channel = (data ?? {}) as Partial<V3ShowChannelResponse> &
        Partial<ChannelV2PublicDetailResponse>;
      return {
        id: channel.id,
        name: channel.name,
        slug: channel.slug,
        gallery_color: channel.gallery_color,
        // The gallery falls back to it when gallery_color is unset, so the
        // password gate does too.
        app_background_color: channel.app_background_color,
        gallery_banner: channel.gallery_banner,
        // `big_logo_image_url` is the wire key for BOTH versions. rails' ChannelMapper
        // reads `shape[:big_logo_image]` and writes `big_logo_image_url`
        // (movebooth-rails app/mappers/channel_mapper.rb:15), for v3
        // SHOW_CHANNEL_SHAPE and v2 PUBLIC_CHANNEL_SHAPE alike. The vendored spec
        // (movebooth-rails #1346) now correctly names the emitted field
        // `big_logo_image_url` for v3 as well, so no fallback to a spec-only
        // `big_logo_image` key is needed anymore.
        big_logo_image_url: channel.big_logo_image_url,
        // The hero headline and its colour, which the password gate shows.
        header: channel.header,
        header_color: channel.header_color,
      };
    },
  });
};

/** The slice of the session user this hook reads (`state.app.user`). */
interface SessionUser {
  id?: number;
  authToken?: string;
}

/**
 * Whether the signed-in caller may delete photos in this gallery.
 *
 * Photo deletion (`DELETE /api/v2/gifs/:id`, GifPolicy#destroy?) is allowed for
 * a super_admin, the channel owner, or a channel admin. The authenticated
 * `GET /api/v2/channels/:identifier` is authorized by ChannelPolicy#show? —
 * the same three — and answers 403 to anyone else, so it is the server's
 * answer to "does this caller manage this channel". A token that no longer
 * resolves is read as anonymous and gets 200 with the PUBLIC shape, which never
 * carries the capability flags; only the member shape (ChannelV2DetailResponse)
 * does. So "may delete" = the read succeeded AND returned `can_delete`. Its
 * VALUE is not the answer: it is CHANNEL deletion (owner/super_admin), and a
 * channel admin gets `false` there yet may delete photos.
 *
 * Fails closed: signed out → no request; any error (including a failed
 * revalidation after an earlier success) or the public shape → false.
 */
export const useCanManageGallery = (identifier?: string): boolean => {
  const user = useSelector(
    (state: { app: { user?: SessionUser | null } }) => state.app.user
  );
  const authToken = user?.authToken;

  const { data, status } = useQuery<unknown, ApiError, boolean>({
    // Keyed by user id, never the token. `cacheTime: 0` drops the answer as
    // soon as no gallery is mounted, so a later session (even the same user
    // signing back in) always asks again rather than reusing it.
    queryKey: ["canManageGallery", identifier, user?.id],
    queryFn: () =>
      // `enabled` guarantees a token whenever this runs.
      authedRequest(`v2/channels/${identifier}`, authToken as string, { method: "GET" }),
    enabled: !!identifier && !!authToken,
    // A 403 is the expected answer for a non-manager; nothing here is worth a retry.
    retry: false,
    // Access can be revoked while the page is open: re-ask on focus/remount.
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: true,
    select: (channel: unknown): boolean =>
      typeof (channel as Partial<ChannelV2DetailResponse> | null)?.can_delete ===
      "boolean",
  });

  // `status` (not just `data`): a failed REVALIDATION keeps the previous
  // `data`, and a revoked manager must lose the controls, not keep them.
  return !!authToken && status === "success" && data === true;
};

/**
 * Type guard: an empty-gallery v3 response carries `channel` and has no
 * `pagination`, while a success response always carries `pagination` (+ `data`)
 * and never carries `channel`. We discriminate on the structural difference
 * (`channel` present, `pagination` absent) rather than on `message`, which the
 * schema marks optional — an empty response that omits `message` must still be
 * recognized so its `channel` isn't silently dropped into the success path.
 */
const isEmptyGalleryResponse = (
  res: V3GalleryResponse
): res is V3GalleryEmptyResponse =>
  "channel" in res && res.channel != null && !("pagination" in res);

/**
 * Transform v3 API response to match v2 format expected by UI
 */
const transformV3Response = (
  v3Data: V3GalleryResponse,
  originalParams?: GalleryQueryParams
): GalleryViewModel => {
  // Handle date-filtered empty response edge case
  // v3 returns { channel: {...}, message: "..." } when no GIFs match date range
  if (isEmptyGalleryResponse(v3Data)) {
    return {
      current_page: Number(originalParams?.page) || 0,
      page_count: 0,
      total_count: 0,
      gifs: [],
      channel: (v3Data.channel as Record<string, unknown>) || {},
    };
  }

  // Standard v3 response structure
  const { pagination, data } = v3Data;

  // Calculate page_count from pagination data
  const pageCount = pagination?.total
    ? Math.ceil(pagination.total / (pagination.perPage || 30))
    : 0;

  // Separate gifs from channel data
  const { gifs, ...channelData } = data || {};

  // Transform GIF objects to include image_url fields for different contexts
  // v3 returns image_urls object with size variants
  const transformedGifs = (gifs || []).map((gif: V3Gif) => ({
    ...gif,
    // grid_image_url: Used for gallery grid view (optimize for performance/bandwidth)
    // Fallback chain: medium -> big -> huge -> original
    grid_image_url:
      gif.image_urls?.medium ||
      gif.image_urls?.big ||
      gif.image_urls?.huge ||
      gif.image_urls?.original,
    // image_url: Used for lightbox/single view (prioritize quality)
    // Fallback chain: original -> huge -> big -> medium
    image_url:
      gif.image_urls?.original ||
      gif.image_urls?.huge ||
      gif.image_urls?.big ||
      gif.image_urls?.medium,
  }));

  return {
    // Use 0-based currentPage consistently (no conversion needed)
    current_page: pagination?.currentPage ?? 0,
    page_count: pageCount,
    has_more: pagination?.hasMore ?? false,
    total_count: pagination?.total ?? 0,
    gifs: transformedGifs,
    channel: channelData as Record<string, unknown>,
  };
};
